export type PhoneVerificationState = {
  phoneNumber: string | null;
  verified: boolean;
};

const PHONE_VERIFICATION_STORAGE_KEY = "nommia.phoneVerification";

const DEFAULT_STATE: PhoneVerificationState = {
  phoneNumber: null,
  verified: false,
};

const normalizePhoneNumber = (value: unknown) => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const readState = (): PhoneVerificationState => {
  if (typeof window === "undefined") {
    return DEFAULT_STATE;
  }

  try {
    const stored = window.localStorage.getItem(PHONE_VERIFICATION_STORAGE_KEY);
    if (!stored) {
      return DEFAULT_STATE;
    }

    const parsed = JSON.parse(stored) as Partial<PhoneVerificationState>;
    return {
      verified: Boolean(parsed?.verified),
      phoneNumber: normalizePhoneNumber(parsed?.phoneNumber) ?? null,
    };
  } catch (error) {
    console.warn(
      "[auth] Unable to read phone verification state from storage",
      error
    );
    return DEFAULT_STATE;
  }
};

const writeState = (state: PhoneVerificationState) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      PHONE_VERIFICATION_STORAGE_KEY,
      JSON.stringify(state)
    );
  } catch (error) {
    console.warn(
      "[auth] Unable to persist phone verification state to storage",
      error
    );
  }
};

export const getPhoneVerificationState = (): PhoneVerificationState =>
  readState();

export const setPhoneVerificationState = (
  state: PhoneVerificationState
): PhoneVerificationState => {
  const normalized: PhoneVerificationState = {
    verified: Boolean(state.verified),
    phoneNumber: normalizePhoneNumber(state.phoneNumber),
  };
  writeState(normalized);
  return normalized;
};

export const updatePhoneVerificationState = (
  updates: Partial<PhoneVerificationState>
): PhoneVerificationState => {
  const current = getPhoneVerificationState();
  return setPhoneVerificationState({
    verified:
      typeof updates.verified === "boolean"
        ? updates.verified
        : current.verified,
    phoneNumber:
      updates.phoneNumber !== undefined
        ? updates.phoneNumber
        : current.phoneNumber,
  });
};

export const clearPhoneVerificationState = () => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(PHONE_VERIFICATION_STORAGE_KEY);
  } catch (error) {
    console.warn(
      "[auth] Unable to clear phone verification state from storage",
      error
    );
  }
};

export const isPhoneVerified = () => getPhoneVerificationState().verified;
